import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';

import { ThemeFamily, ThemeFamilyOption } from '@core/services/theme-primary-themes';
import { ThemeSelectionService } from '@core/services/theme-selection.service';

import { SidebarIconComponent } from './sidebar-icon.component';

@Component({
  selector: 'app-sidebar-theme-picker',
  standalone: true,
  imports: [FormsModule, PopoverModule, RippleModule, SelectModule, TooltipModule, SidebarIconComponent],
  templateUrl: './sidebar-theme-picker.component.html',
  styleUrl: './sidebar-theme-picker.component.scss',
})
export class SidebarThemePickerComponent {
  readonly collapsed = input(false);

  private readonly themeSelectionService = inject(ThemeSelectionService);
  private readonly visibleFamilyState = signal<ThemeFamily>(this.themeSelectionService.selectedTheme().family);

  readonly activeFamily = this.visibleFamilyState.asReadonly();
  readonly selectedTheme = this.themeSelectionService.selectedTheme;
  readonly selectedThemeId = this.themeSelectionService.selectedThemeId;
  readonly themeFamilies = [...this.themeSelectionService.themeFamilies];
  readonly themesByFamily = this.themeSelectionService.themeOptionsByFamily;
  readonly selectedFamilyOption = computed<ThemeFamilyOption | null>(
    () => this.themeFamilies.find((family) => family.id === this.activeFamily()) ?? null
  );
  readonly previewStops: ReadonlyArray<100 | 300 | 500 | 700 | 950> = [
    100,
    300,
    500,
    700,
    950,
  ];

  readonly collapsedTooltip = computed(() => `Kolor akcentu: ${this.selectedTheme().label}`);

  constructor() {
    effect(() => {
      this.visibleFamilyState.set(this.selectedTheme().family);
    });
  }

  selectTheme(themeId: string): void {
    this.themeSelectionService.applyTheme(themeId);
  }

  selectFamily(family: ThemeFamily): void {
    this.visibleFamilyState.set(family);
  }

  onFamilySelectionChange(event: SelectChangeEvent): void {
    const family = event.value as ThemeFamilyOption | null;

    if (!family) {
      return;
    }

    this.selectFamily(family.id);
  }
}
